// Getting a chosen file ready to upload. The picture a shared link shows is
// drawn at about 1200 x 630 and never larger, so anything wider is bytes
// nobody sees — and every image is kept in Postgres, inside the nightly dump.
// A photo straight off a phone is several megabytes, and some apps (WhatsApp
// among them) skip a preview image that heavy.
//
// So a file is redrawn at IMAGE_MAX_WIDTH as a WebP, which is a good deal
// smaller than the JPEG the same picture makes and, unlike it, keeps what was
// transparent. A browser that cannot write a WebP gets a JPEG. The file as
// chosen is kept only when it is already narrow enough and the redraw came out
// no smaller — a picture already saved well is not worth encoding twice.
//
// The server applies the same limits to whatever reaches it, since this is not
// the only way an image gets in. Here they save the upload, not just the row.
import { IMAGE_MAX_WIDTH } from "@bibleunleashed/shared";

const TAKEN = ["image/png", "image/jpeg", "image/webp"];
const QUALITY = 0.82;

type Encoding = "image/webp" | "image/jpeg";

/** The bitmap drawn at this size and encoded, or null if the browser won't write that format. */
async function encode(bitmap: ImageBitmap, width: number, height: number, type: Encoding): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  if (type === "image/jpeg") {
    // A JPEG has no transparency; what was clear shows white rather than black.
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY));
  // A browser missing the format asked for quietly answers with a PNG instead.
  return blob?.type === type ? blob : null;
}

/** The file redrawn for upload — or the file itself, where redrawing it would gain nothing. */
export async function prepareImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, IMAGE_MAX_WIDTH / bitmap.width);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const blob = (await encode(bitmap, width, height, "image/webp")) ?? (await encode(bitmap, width, height, "image/jpeg"));
    if (!blob) throw new Error("This browser couldn’t shrink that image");
    const worthKeeping = TAKEN.includes(file.type) && bitmap.width <= IMAGE_MAX_WIDTH && blob.size >= file.size;
    return worthKeeping ? file : blob;
  } finally {
    bitmap.close();
  }
}
